import { useCallback, useState } from "react";
import { DEFAULT_OPTIONS } from "service/HttpService";
import NotificationPopUp from "util/NotificationPopUp";
import { OpcUaUserDTO } from "../../../serviceapi/dataCollectorApi";
import { useOpcUaApi } from "./useOpcUaApi";

/** CRUD for OPC UA username/password accounts (/rest/opcua/users). */
export const useOpcUaUsers = () => {
  const { opcUaApi } = useOpcUaApi();

  const [users, setUsers] = useState<OpcUaUserDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await opcUaApi.getOpcUaUsers(DEFAULT_OPTIONS);
      setUsers(response.data ?? []);
    } catch (error) {
      NotificationPopUp.handleGenericError(error, "Failed to fetch OPC UA users.");
    } finally {
      setIsLoading(false);
    }
  }, [opcUaApi]);

  /** PUT is create-or-replace, so this covers both adding and overwriting an account. */
  const saveUser = useCallback(
    async (username: string, password: string, enabled: boolean) => {
      try {
        await opcUaApi.putOpcUaUser(username, { password, enabled }, DEFAULT_OPTIONS);
        NotificationPopUp.show(`User "${username}" saved.`, "success");
        await fetchUsers();
        return true;
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          `Failed to save user "${username}".`
        );
        return false;
      }
    },
    [opcUaApi, fetchUsers]
  );

  /**
   * `enabled` is sent alongside the new password because the endpoint takes a whole
   * OpcUaUserRequest — omitting it risks the backend reading it as false and disabling
   * the account as a side effect of a password change.
   */
  const changePassword = useCallback(
    async (username: string, password: string, enabled: boolean) => {
      try {
        await opcUaApi.setOpcUaUserPassword(
          username,
          { password, enabled },
          DEFAULT_OPTIONS
        );
        NotificationPopUp.show(`Password changed for "${username}".`, "success");
        return true;
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          `Failed to change password for "${username}".`
        );
        return false;
      }
    },
    [opcUaApi]
  );

  const setUserEnabled = useCallback(
    async (username: string, enabled: boolean) => {
      try {
        await opcUaApi.setOpcUaUserEnabled(username, enabled, DEFAULT_OPTIONS);
        NotificationPopUp.show(
          `User "${username}" ${enabled ? "enabled" : "disabled"}.`,
          "success"
        );
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          `Failed to ${enabled ? "enable" : "disable"} user "${username}".`
        );
      } finally {
        // Refetch either way so the switch reflects the server, not the optimistic click.
        await fetchUsers();
      }
    },
    [opcUaApi, fetchUsers]
  );

  const deleteUser = useCallback(
    async (username: string) => {
      try {
        await opcUaApi.deleteOpcUaUser(username, DEFAULT_OPTIONS);
        NotificationPopUp.show(`User "${username}" deleted.`, "success");
        await fetchUsers();
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          `Failed to delete user "${username}".`
        );
      }
    },
    [opcUaApi, fetchUsers]
  );

  return {
    users,
    isLoading,
    fetchUsers,
    saveUser,
    changePassword,
    setUserEnabled,
    deleteUser,
  };
};
